import React from "react";
import { Link, Routes, Route, useLocation } from "react-router-dom";
import PageHero from "../components/PageHero";
import {
  ShieldCheck,
  ClipboardCheck,
  Ambulance,
  Syringe,
  Activity,
  Salad,
  ArrowUpRight,
  CheckCircle2,
  Phone,
} from "lucide-react";
import { SERVICES, TPA_PARTNERS, HOSPITAL } from "../data/site";

const ICONS = { ShieldCheck, ClipboardCheck, Ambulance, Syringe, Activity, Salad };

function ServicesIndex() {
  return (
    <>
      <PageHero
        eyebrow="Services"
        title={<>Care services that go <span className="italic text-brand-maroon">beyond the consult.</span></>}
        subtitle="From cashless TPA support to vaccinations, ambulance and structured health check-ups — designed around your family's life."
        image="https://images.pexels.com/photos/8442027/pexels-photo-8442027.jpeg"
      />

      <section className="max-w-7xl mx-auto px-5 md:px-10 py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {SERVICES.map((s) => {
            const Icon = ICONS[s.icon] || ShieldCheck;
            const isTPA = s.key === "tpa";
            return (
              <article
                key={s.key}
                data-testid={`service-card-${s.key}`}
                className="group relative rounded-3xl bg-white border border-brand-beige p-7 hover:shadow-lg transition"
              >
                <div className="w-12 h-12 rounded-xl bg-brand-maroon/10 text-brand-maroon flex items-center justify-center">
                  <Icon size={20} />
                </div>
                <h3 className="font-serif-display text-2xl text-brand-ink mt-5">{s.title}</h3>
                <p className="text-sm text-brand-ink/65 mt-2 leading-relaxed">{s.summary}</p>

                {isTPA && (
                  <Link
                    to="/services/tpa"
                    data-testid="tpa-detail-link"
                    className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-brand-maroon"
                  >
                    See TPA partners <ArrowUpRight size={15} />
                  </Link>
                )}
              </article>
            );
          })}
        </div>
      </section>
    </>
  );
}

function TPAPage() {
  return (
    <>
      <PageHero
        eyebrow="Services • TPA"
        title={<>Cashless hospitalization, <span className="italic text-brand-maroon">made simple.</span></>}
        subtitle="Show your card at admission and our TPA helpdesk takes care of the rest. We are empaneled with leading insurers and TPAs."
        image="https://images.unsplash.com/photo-1587351021759-3e566b6af7cc?w=1400&q=85"
      />

      <section className="max-w-7xl mx-auto px-5 md:px-10 py-16 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-7">
          <h2 className="font-serif-display text-3xl md:text-4xl text-brand-ink">How cashless works</h2>
          <ol className="mt-6 space-y-4">
            {[
              "Show your TPA / insurance card and a photo ID at admission.",
              "Our TPA desk fills out the pre-authorization form and submits it to your insurer.",
              "Approval typically arrives in 2–6 hours for planned admissions, or within 1 hour for emergencies.",
              "At discharge, you only pay for items not covered by the policy (consumables, special diet, etc.).",
            ].map((s, i) => (
              <li key={i} className="flex gap-4">
                <span className="shrink-0 w-7 h-7 rounded-full bg-brand-maroon text-white text-xs font-bold flex items-center justify-center">{i + 1}</span>
                <span className="text-brand-ink/70 leading-relaxed">{s}</span>
              </li>
            ))}
          </ol>

          <div className="mt-10 rounded-2xl bg-brand-beige p-6 border border-brand-beige">
            <div className="text-xs uppercase tracking-[0.22em] font-bold text-brand-maroon">TPA Helpdesk</div>
            <div className="mt-2 font-serif-display text-2xl text-brand-ink">Call before admission</div>
            <p className="text-sm text-brand-ink/65 mt-1">
              We'll verify eligibility for your specific policy and walk you through the documents needed.
            </p>
            <a
              href={`tel:${HOSPITAL.phoneDial}`}
              data-testid="tpa-helpdesk-call"
              className="mt-4 inline-flex items-center gap-2 rounded-full bg-brand-maroon text-white font-semibold px-5 py-3 hover:bg-brand-maroonDark transition"
            >
              <Phone size={15} /> {HOSPITAL.phone}
            </a>
          </div>
        </div>

        <aside className="md:col-span-5">
          <div className="rounded-3xl bg-white border border-brand-beige p-8">
            <div className="text-xs uppercase tracking-[0.22em] font-bold text-brand-maroon">Empaneled partners</div>
            <h3 className="font-serif-display text-2xl text-brand-ink mt-2">Insurance & TPAs we work with</h3>
            <ul className="mt-6 space-y-3">
              {TPA_PARTNERS.map((p) => (
                <li
                  key={p.name}
                  data-testid={`tpa-partner-${p.name.replace(/\s+/g, "-").toLowerCase()}`}
                  className="flex items-center gap-3 border-b border-brand-ink/10 pb-3 last:border-0"
                >
                  <CheckCircle2 size={18} className="text-brand-maroon shrink-0" />
                  <span className="text-brand-ink font-medium">{p.name}</span>
                </li>
              ))}
            </ul>
            <p className="text-xs text-brand-ink/55 mt-6">
              List indicative. Coverage and approvals depend on your individual policy. Please call to confirm before
              admission.
            </p>
          </div>
        </aside>
      </section>
    </>
  );
}

export default function Services() {
  const { pathname } = useLocation();
  return (
    <div data-testid="services-page">
      <Routes>
        <Route path="/" element={<ServicesIndex />} />
        <Route path="/tpa" element={<TPAPage />} />
      </Routes>
      {/* anchor for back link */}
      {pathname !== "/services" && (
        <div className="max-w-7xl mx-auto px-5 md:px-10 pb-12">
          <Link
            to="/services"
            data-testid="back-to-services"
            className="text-sm font-semibold text-brand-maroon hover:underline"
          >
            ← Back to all services
          </Link>
        </div>
      )}
    </div>
  );
}
